import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Pagination,
  Chip,
  InputAdornment,
  IconButton,
  Container,
  Grid,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';

const H1BDataExplorer = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    employerName: '',
    fiscalYear: '',
    taxId: '',
    industryCode: '',
    city: '',
    state: '',
    zipCode: ''
  });

  // Replace with your actual API URL
  const API_URL = `${process.env.REACT_APP_BACK_END_URL}/dbdata`;

  // Function to fetch data based on current state
  const fetchData = async () => {
    setLoading(true);
    try {
      // Create query params based on filters
      const params = {
        page: page,
        limit: 10
      };

      // Add non-empty filters
      if (filters.employerName.trim()) {
        params.query = filters.employerName;
      }

      // Choose endpoint based on whether we're searching or not
      const endpoint = filters.employerName.trim() ? '/search/' : '/preview/';
      const response = await axios.get(`${API_URL}${endpoint}`, { params });

      setData(response.data.results);
      setTotalPages(response.data.pages);
      setTotalResults(response.data.total);
      setError(null);
    } catch (err) {
      setError('Error fetching data. Please try again later.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchData();
  }, [page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const applyFilters = () => {
    setPage(1); // Reset to first page
    fetchData();
  };

  const clearFilters = () => {
    setFilters({
      employerName: '',
      fiscalYear: '',
      taxId: '',
      industryCode: '',
      city: '',
      state: '',
      zipCode: ''
    });
    setPage(1);
    fetchData();
  };

  // Format the employer name for display
  const formatEmployerName = (name) => {
    return name || 'N/A';
  };

  // Debug function to check column names
  const logFirstRow = () => {
    if (data.length > 0) {
      console.log("First row data:", data[0]);
    }
  };

  // Optional client-side filtering for non-employer name fields
  const filteredData = data.filter(row => {
    return (
      // If fiscal year filter is applied, check if the row matches
      (filters.fiscalYear === '' || String(row["Fiscal Year"]) === filters.fiscalYear) &&
      // Tax ID filter
      (filters.taxId === '' || (row["Tax ID"] && String(row["Tax ID"]).includes(filters.taxId))) &&
      // Industry code filter
      (filters.industryCode === '' ||
        (row["Industry (NAICS) Code"] && String(row["Industry (NAICS) Code"]).includes(filters.industryCode))) &&
      // City filter
      (filters.city === '' ||
        (row["Petitioner City"] && String(row["Petitioner City"]).toLowerCase().includes(filters.city.toLowerCase()))) &&
      // State filter
      (filters.state === '' ||
        (row["Petitioner State"] && String(row["Petitioner State"]).toLowerCase().includes(filters.state.toLowerCase()))) &&
      // Zip code filter
      (filters.zipCode === '' ||
        (row["Petitioner Zip Code"] && String(row["Petitioner Zip Code"]).includes(filters.zipCode)))
    );
  });

  // Use effect to help debug column names
  useEffect(() => {
    if (data.length > 0) {
      logFirstRow();
    }
  }, [data]);

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header with title and compact info */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
          H-1B Visa Data Explorer
          <Typography component="span" variant="subtitle1" color="text.secondary" sx={{ ml: 2 }}>
            ({totalResults.toLocaleString()} records found)
          </Typography>
        </Typography>
      </Box>

      {/* Filters section */}
      <Paper elevation={2} sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            <FilterListIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
            Search & Filters
          </Typography>
          {/* <Button
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FilterListIcon />}
          >
            {showFilters ? 'Hide' : 'Show'} Advanced Filters
          </Button> */}
        </Box>

        {/* Primary Search */}
        <TextField
          fullWidth
          size="small"
          name="employerName"
          label="Employer Name"
          variant="outlined"
          value={filters.employerName}
          onChange={handleFilterChange}
          placeholder="Enter employer name to search..."
          sx={{ mb: showFilters ? 2 : 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filters.employerName && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={() => handleFilterChange({ target: { name: 'employerName', value: '' } })}
                  edge="end"
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {/* Advanced Filters (collapsible) */}
        {showFilters && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                name="fiscalYear"
                label="Fiscal Year"
                variant="outlined"
                value={filters.fiscalYear}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                name="taxId"
                label="Tax ID"
                variant="outlined"
                value={filters.taxId}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                name="industryCode"
                label="Industry Code"
                variant="outlined"
                value={filters.industryCode}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                name="city"
                label="City"
                variant="outlined"
                value={filters.city}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                name="state"
                label="State"
                variant="outlined"
                value={filters.state}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                name="zipCode"
                label="Zip Code"
                variant="outlined"
                value={filters.zipCode}
                onChange={handleFilterChange}
              />
            </Grid>
          </Grid>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={clearFilters}
            startIcon={<RefreshIcon />}
            sx={{ mr: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={applyFilters}
            startIcon={<SearchIcon />}
          >
            Apply Filters
          </Button>
        </Box>
      </Paper>

      {/* Loading indicator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress size={30} thickness={4} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Status Chip */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Chip
              size="small"
              icon={filters.employerName ? <SearchIcon /> : null}
              label={filters.employerName ? `Results for: "${filters.employerName}"` : 'All records'}
              color={filters.employerName ? "primary" : "default"}
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              Page {page} of {totalPages || 1}
            </Typography>
          </Box>

          {/* Data Table */}
          <TableContainer component={Paper} elevation={1} sx={{ overflow: 'auto' }}>
            <Table sx={{ minWidth: 1200 }} size="small" aria-label="H-1B visa data table">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell>Line #</TableCell>
                  <TableCell>Fiscal Year</TableCell>
                  <TableCell>Employer Name</TableCell>
                  <TableCell>Tax ID</TableCell>
                  <TableCell>Industry Code</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell align="right">Initial Approval</TableCell>
                  <TableCell align="right">Initial Denial</TableCell>
                  <TableCell align="right">Continuing Approval</TableCell>
                  <TableCell align="right">Continuing Denial</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{row["Line by line"]}</TableCell>
                      <TableCell>{row["Fiscal Year"]}</TableCell>
                      <TableCell className="employer-name-cell" sx={{ maxWidth: 200, whiteSpace: 'normal' }}>
                        {formatEmployerName(row["Employer (Petitioner) Name"])}
                      </TableCell>
                      <TableCell>{row["Tax ID"]}</TableCell>
                      <TableCell>{row["Industry (NAICS) Code"]}</TableCell>
                      <TableCell>{row["Petitioner City"]}</TableCell>
                      <TableCell>{row["Petitioner State"]}</TableCell>
                      <TableCell>{row["Petitioner Zip Code"]}</TableCell>
                      <TableCell align="right">{row["Initial Approval"]}</TableCell>
                      <TableCell align="right">{row["Initial Denial"]}</TableCell>
                      <TableCell align="right">{row["Continuing Approval"]}</TableCell>
                      <TableCell align="right">{row["Continuing Denial"]}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No matching records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
                disabled={loading}
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
            <Typography variant="caption" color="text.secondary">
              H-1B visa application data explorer. Search is based on employer names.
            </Typography>
          </Box>
        </>
      )}
    </Container>
  );
};

export default H1BDataExplorer;